import React, { useState, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import { useLang } from "../lib/lang";
import { api } from "../lib/api";

function uuid() {
  return Math.random().toString(36).slice(2) + Date.now().toString(36);
}

/* Render markdown links [label](url) and **bold** inside chat bubbles.
   Internal routes (starting with /) become <Link>, externals open in new tab. */
function renderRich(text) {
  if (!text) return null;
  const linkRe = /\[([^\]]+)\]\(([^)]+)\)/g;
  const boldRe = /\*\*([^*]+)\*\*/g;
  const out = [];
  let lastIdx = 0;
  let key = 0;

  // First pass: split by links
  const matches = [...text.matchAll(linkRe)];
  if (matches.length === 0) {
    // Just handle bold
    return renderBold(text);
  }
  for (const m of matches) {
    const start = m.index;
    const end = start + m[0].length;
    if (start > lastIdx) {
      out.push(<span key={key++}>{renderBold(text.slice(lastIdx, start))}</span>);
    }
    const label = m[1];
    const href = m[2];
    if (href.startsWith("/")) {
      out.push(
        <Link key={key++} to={href} className="chat-link" data-testid="chat-link">
          {label}
        </Link>
      );
    } else {
      out.push(
        <a key={key++} href={href} target="_blank" rel="noopener noreferrer" className="chat-link" data-testid="chat-link">
          {label}
        </a>
      );
    }
    lastIdx = end;
  }
  if (lastIdx < text.length) {
    out.push(<span key={key++}>{renderBold(text.slice(lastIdx))}</span>);
  }
  return out;
}

function renderBold(text) {
  const boldRe = /\*\*([^*]+)\*\*/g;
  const out = [];
  let lastIdx = 0;
  let key = 0;
  for (const m of text.matchAll(boldRe)) {
    if (m.index > lastIdx) out.push(<span key={key++}>{text.slice(lastIdx, m.index)}</span>);
    out.push(<strong key={key++}>{m[1]}</strong>);
    lastIdx = m.index + m[0].length;
  }
  if (lastIdx < text.length) out.push(<span key={key++}>{text.slice(lastIdx)}</span>);
  return out;
}

export default function ChatWidget() {
  const { t, lang } = useLang();
  const [open, setOpen] = useState(false);
  const [sessionId] = useState(() => {
    try {
      const k = localStorage.getItem("bb_chat_session");
      if (k) return k;
      const id = uuid();
      localStorage.setItem("bb_chat_session", id);
      return id;
    } catch { return uuid(); }
  });
  const [msgs, setMsgs] = useState(() => {
    try {
      const raw = localStorage.getItem("bb_chat_msgs");
      return raw ? JSON.parse(raw) : [];
    } catch { return []; }
  });
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const bottomRef = useRef(null);
  const bodyRef = useRef(null);

  useEffect(() => {
    try { localStorage.setItem("bb_chat_msgs", JSON.stringify(msgs.slice(-30))); } catch (err) { void err; }
    // Only auto-scroll to bottom when there are real messages — otherwise the
    // greeting bubble gets hidden under the header on first open.
    if (msgs.length > 0) {
      bottomRef.current?.scrollIntoView({ behavior: "smooth" });
    }
  }, [msgs]);

  // When the panel opens, force scroll to top so the greeting is fully visible
  useEffect(() => {
    if (!open) return;
    // Run after the panel finishes animating so layout is final
    const ids = [
      requestAnimationFrame(() => { if (bodyRef.current) bodyRef.current.scrollTop = 0; }),
      setTimeout(() => { if (bodyRef.current) bodyRef.current.scrollTop = 0; }, 60),
      setTimeout(() => { if (bodyRef.current) bodyRef.current.scrollTop = 0; }, 320),
    ];
    return () => {
      cancelAnimationFrame(ids[0]);
      clearTimeout(ids[1]);
      clearTimeout(ids[2]);
    };
  }, [open]);

  const greet = lang === "es"
    ? "Hola 👋 Soy el asistente B&B. ¿En qué te ayudo hoy?"
    : "Hi 👋 I'm the B&B assistant. How can I help you today?";

  const send = async (e) => {
    e?.preventDefault?.();
    const text = input.trim();
    if (!text || sending) return;
    setInput("");
    setMsgs((m) => [...m, { role: "user", body: text, ts: Date.now() }]);
    setSending(true);
    try {
      const { data } = await api.post("/chat", { session_id: sessionId, message: text });
      setMsgs((m) => [...m, { role: "assistant", body: data.reply, ts: Date.now() }]);
    } catch (err) {
      console.error("Chat failed:", err);
      setMsgs((m) => [...m, { role: "assistant", body: t("Sorry, I couldn't process that. Please try again.", "Lo siento, no pude procesar eso. Intenta de nuevo."), ts: Date.now() }]);
    } finally {
      setSending(false);
    }
  };

  // Quick-start chips offered before the user types anything
  const chips = lang === "es"
    ? [
        { q: "¿Qué servicio me conviene?", testid: "chat-chip-recommend" },
        { q: "¿Cuál es el precio de Desarrollo de marca?", testid: "chat-chip-pricing" },
        { q: "Quiero empezar ahora", testid: "chat-chip-start" },
      ]
    : [
        { q: "Which service is right for me?", testid: "chat-chip-recommend" },
        { q: "What is the price of Brand Development?", testid: "chat-chip-pricing" },
        { q: "I want to start now", testid: "chat-chip-start" },
      ];

  const sendChip = (q) => {
    setInput(q);
    setTimeout(() => {
      const form = document.querySelector(".chat-input");
      form?.requestSubmit?.();
    }, 50);
  };

  return (
    <>
      <button
        type="button"
        className={`chat-fab${open ? " open" : ""}`}
        onClick={() => setOpen((o) => !o)}
        data-testid="chat-fab"
        aria-label={open ? "Close chat" : "Open chat"}
      >
        {open ? "×" : (
          <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
            <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z" />
          </svg>
        )}
      </button>
      {open && (
        <div className="chat-panel" data-testid="chat-panel">
          <header className="chat-header">
            <div className="chat-title" data-testid="chat-title">{t("B&B assistant", "Asistente B&B")}</div>
            <button type="button" className="chat-close" onClick={() => setOpen(false)} aria-label="Close">×</button>
          </header>
          <div className="chat-body" data-testid="chat-body" ref={bodyRef}>
            {msgs.length === 0 && (
              <div className="msg msg-bot">
                <div className="bubble">{greet}</div>
              </div>
            )}
            {msgs.length === 0 && !sending && (
              <div className="chat-chips" data-testid="chat-chips">
                {chips.map((c) => (
                  <button
                    key={c.testid}
                    type="button"
                    className="chat-chip"
                    onClick={() => sendChip(c.q)}
                    data-testid={c.testid}
                  >
                    {c.q}
                  </button>
                ))}
              </div>
            )}
            {msgs.map((m) => (
              <div key={`${m.ts}-${m.role}`} className={`msg ${m.role === "user" ? "msg-user" : "msg-bot"}`}>
                <div className="bubble">{renderRich(m.body)}</div>
              </div>
            ))}
            {sending && (
              <div className="msg msg-bot">
                <div className="bubble bubble-typing"><span /><span /><span /></div>
              </div>
            )}
            <div ref={bottomRef} />
          </div>
          <form className="chat-input" onSubmit={send}>
            <input
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder={t("Ask anything…", "Pregunta lo que quieras…")}
              data-testid="chat-input"
              disabled={sending}
            />
            <button type="submit" className="chat-send" disabled={sending || !input.trim()} data-testid="chat-send">→</button>
          </form>
        </div>
      )}
    </>
  );
}
